import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Table } from '@shared/components';
import { formatIsoDateOnlyShort } from '@/app/utils/formatDate';
import { formatVerbatimMoneyCurrency } from '@shared/utils/formatMoneyDisplay';
import {
    signedAmounts,
    statusOf,
    type AmountCells,
    type StatementRowView,
} from '../../utils/statementRows';
import { StatementStatusPill } from './StatementStatusPill';

interface Props {
    rows: StatementRowView[];
    currency: string;
    asOf: string;
    showType: boolean;
}

const AMOUNT_PARTS = [
    { key: 'net', label: 'Net' },
    { key: 'vat', label: 'VAT' },
    { key: 'gross', label: 'Gross' },
] as const satisfies ReadonlyArray<{ key: keyof AmountCells; label: string }>;

const STATEMENT_COLUMN_WIDTHS = {
    date: 110,
    type: 80,
    description: 280,
    net: 115,
    vat: 115,
    gross: 115,
    status: 120,
};

const EDGE_TO_EDGE_TABLE_CLASSES = [
    'rounded-none! border-x-0! border-b-0! shadow-none!',
    '[&_thead]:bg-mp-background [&_thead]:border-b [&_thead]:border-mp-border',
    '[&_tbody]:bg-transparent [&_th:first-child]:pl-6 [&_td:first-child]:pl-6 [&_th:last-child]:pr-6 [&_td:last-child]:pr-6',
].join(' ');

const mutedClass = 'text-mp-muted';

const TYPE_COLUMN: ColumnDef<StatementRowView> = {
    id: 'type',
    header: 'Type',
    cell: ({ row }) => <span className={mutedClass}>{row.original.row.transactionCode ?? '—'}</span>,
};

export function AgreementStatementDataTable({ rows, currency, asOf, showType }: Props) {
    const columns = useMemo<ColumnDef<StatementRowView>[]>(
        () => [
            { id: 'date', header: 'Date', cell: ({ row }) => formatIsoDateOnlyShort(row.original.row.date) },
            ...(showType ? [TYPE_COLUMN] : []),
            {
                id: 'description',
                header: 'Description',
                cell: ({ row }) => row.original.row.description,
            },
            ...AMOUNT_PARTS.map(
                ({ key, label }): ColumnDef<StatementRowView> => ({
                    id: key,
                    header: () => <span className="ml-auto">{label}</span>,
                    cell: ({ row }) => (
                        <AmountCell
                            value={signedAmounts(row.original.row)[key]}
                            currency={currency}
                            bold={key === 'gross'}
                            muted={key === 'vat'}
                        />
                    ),
                }),
            ),
            {
                id: 'status',
                header: 'Status',
                cell: ({ row }) => <StatementStatusPill status={statusOf(row.original.row, asOf)} />,
            },
        ],
        [currency, asOf, showType],
    );

    return (
        <Table<StatementRowView>
            data={rows}
            columns={columns}
            enableSorting={false}
            getRowId={(entry) => entry.id}
            columnWidths={STATEMENT_COLUMN_WIDTHS}
            className={EDGE_TO_EDGE_TABLE_CLASSES}
        />
    );
}

interface AmountCellProps {
    value: string | null;
    currency: string;
    bold: boolean;
    muted: boolean;
}

function AmountCell({ value, currency, bold, muted }: AmountCellProps) {
    if (value === null) {
        return (
            <span className={`block text-right ${mutedClass}`} title="No VAT breakdown for this transaction">
                —
            </span>
        );
    }
    const tone = bold ? 'font-bold' : muted ? mutedClass : '';
    return <span className={`block text-right ${tone}`.trim()}>{formatVerbatimMoneyCurrency(value, currency)}</span>;
}
